import { ExclamationCircleOutlined, FileTextOutlined } from '@ant-design/icons';
import {
    Button,
    Calendar,
    DatePicker,
    message,
    Modal,
    Space,
    Tabs,
    Tag,
    Typography,
    Checkbox,
} from 'antd';
import { useCallback, useEffect, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import WebGrid from '../../webgrid/src';
import AnnexMergeModal, {
    type AnnexMergePreviewItem,
} from '../components/inventory/AnnexMergeModal';
import PhysicalInventoryPanel from '../components/PhysicalInventoryPanel';
import { useApi } from '../contexts/ApiContext';
import { downloadBlob } from '../utils/downloadBlob';
import { selectInventoryContracts } from '../domain/inventoryMovement';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

interface ExpiredContract {
    Nr: number;
    contract_number: string;
    status: string;
    client_name: string;
    pawned_item_description: string;
    loan_amount_eur: number;
    loan_amount: number;
    expiration_date: string;
    original_due_date: string;
    contract_date: string;
    extended_due_date: string | null;
    duration_days: number;
    calculated_expiration_date: string;
    has_metals: number;
    has_electronics: number;
}

interface ExpiredContractsResponse {
    contracts: ExpiredContract[];
    pagination?: {
        page: number;
        limit: number;
        total: number;
        pages: number;
    };
}

interface MoveToInventoryResponse {
    success: boolean;
    message: string;
    inventoryDate: string;
    processedContracts: number;
    contracts: Array<{
        contractNumber: string;
        newStatus: string;
        hasPreciousMetals: boolean;
        hasElectronicsOrOther: boolean;
        expirationDate: string;
    }>;
}

interface InventoryItem {
    Nr: number;
    contract_number: string;
    status: string;
    client_name: string;
    pawned_item_description: string;
    loan_amount_eur: number;
    loan_amount: number;
    expiration_date: string;
    contract_date: string;
    has_metals: number;
    has_electronics: number;
    inventory_date?: string;
}

interface InventoryItemsResponse {
    contracts: InventoryItem[];
    pagination?: {
        page: number;
        limit: number;
        total: number;
        pages: number;
    };
}

const PHYSICAL_INVENTORY_GRID_HEIGHT = 'calc(100vh - 320px)';

const InventoryMovement = () => {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState('movement');

    // Passive contracts (status '!')
    const [passiveContracts, setPassiveContracts] = useState<ExpiredContract[]>([]);
    const [selectedPassiveContractNumbers, setSelectedPassiveContractNumbers] = useState<string[]>(
        [],
    );
    const [passiveDateRange, setPassiveDateRange] = useState<[Dayjs | null, Dayjs | null]>([
        null,
        null,
    ]);
    const [loadingPassive, setLoadingPassive] = useState(false);

    // Expired contracts (status '!!')
    const [expiredContracts, setExpiredContracts] = useState<ExpiredContract[]>([]);
    const [selectedExpiredContractNumbers, setSelectedExpiredContractNumbers] = useState<string[]>(
        [],
    );
    const [expiredDateRange, setExpiredDateRange] = useState<[Dayjs | null, Dayjs | null]>([
        null,
        null,
    ]);
    const [loadingExpired, setLoadingExpired] = useState(false);

    // Inventory items (status 'INV' or 'EXP')
    const [inventoryItems, setInventoryItems] = useState<InventoryItem[]>([]);
    const [loadingInventory, setLoadingInventory] = useState(false);
    // The Move button spins only while a move is submitted, not while the lists load: the
    // mount-time load toggles too fast for antd's loading-icon leave transition, which then
    // never completes and leaves a hidden "loading" icon in the button's accessible name.
    const [movingToInventory, setMovingToInventory] = useState(false);

    const [inventoryDate, setInventoryDate] = useState<Dayjs>(dayjs());
    const { makeRequest, metals } = useApi();
    const [annexMergeOpen, setAnnexMergeOpen] = useState(false);
    const [annexMergeDates, setAnnexMergeDates] = useState<string[]>([]);
    const [annexMergeSelectedDates, setAnnexMergeSelectedDates] = useState<string[]>([]);
    const [annexMergeDestinationDate, setAnnexMergeDestinationDate] = useState<string | null>(null);
    const [annexMergePreviewItems, setAnnexMergePreviewItems] = useState<AnnexMergePreviewItem[]>([]);
    const [annexMergePreviewLoading, setAnnexMergePreviewLoading] = useState(false);

    const loadContracts = useCallback(
        async (status: '!' | '!!') => {
            try {
                if (status === '!') {
                    setLoadingPassive(true);
                } else {
                    setLoadingExpired(true);
                }

                const queryParams = new URLSearchParams();
                queryParams.append('limit', '1000');
                queryParams.append('status', status);

                const result = await makeRequest<ExpiredContractsResponse>(
                    `/contracts/expired?${queryParams.toString()}`,
                    { method: 'GET' },
                );

                if (result.success && result.data) {
                    if (status === '!') {
                        setPassiveContracts(result.data.contracts || []);
                    } else {
                        setExpiredContracts(result.data.contracts || []);
                    }
                } else {
                    message.error(result.error ?? `Failed to load ${status} contracts`);
                }
            } catch (error) {
                message.error('Network error occurred');
                console.error('Load contracts error:', error);
            } finally {
                if (status === '!') {
                    setLoadingPassive(false);
                } else {
                    setLoadingExpired(false);
                }
            }
        },
        [makeRequest],
    );

    const loadInventoryItems = useCallback(async () => {
        try {
            setLoadingInventory(true);
            const queryParams = new URLSearchParams();
            queryParams.append('limit', '1000');
            // Load only INV status items (electronics/other items for retail sale)
            // EXP status items are precious metals meant for melting, not retail sale
            const invResult = await makeRequest<InventoryItemsResponse>(
                `/contracts/expired?${queryParams.toString()}&status=INV`,
                { method: 'GET' },
            );

            const allItems = invResult.success && invResult.data ? invResult.data.contracts : [];
            setInventoryItems(allItems);
        } catch (error) {
            message.error('Network error occurred');
            console.error('Load inventory items error:', error);
        } finally {
            setLoadingInventory(false);
        }
    }, [makeRequest]);

    useEffect(() => {
        loadContracts('!');
        loadContracts('!!');
        loadInventoryItems();
    }, [loadContracts, loadInventoryItems]);

    // Passive contracts handlers
    const handlePassiveDateRangeChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
        setPassiveDateRange(dates ?? [null, null]);
    };

    const selectAllPassive = () => {
        const allContractNumbers = passiveContracts.map(c => c.contract_number);
        setSelectedPassiveContractNumbers(allContractNumbers);
    };

    const deselectAllPassive = () => {
        setSelectedPassiveContractNumbers([]);
    };

    const handlePassiveRowSelection = (selectedRows: ExpiredContract[]) => {
        const selectedNumbers = selectedRows.map(row => row.contract_number);
        setSelectedPassiveContractNumbers(selectedNumbers);
    };

    const handleSelectPassiveByDateRange = () => {
        const [fromDate, toDate] = passiveDateRange;
        if (!fromDate || !toDate) {
            message.warning(t('inventoryMovement.selectDateRangeFirst'));
            return;
        }

        setSelectedPassiveContractNumbers(selected =>
            selectInventoryContracts(passiveContracts, selected, fromDate, toDate),
        );
    };

    // Expired contracts handlers
    const handleExpiredDateRangeChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
        setExpiredDateRange(dates ?? [null, null]);
    };

    const selectAllExpired = () => {
        const allContractNumbers = expiredContracts.map(c => c.contract_number);
        setSelectedExpiredContractNumbers(allContractNumbers);
    };

    const deselectAllExpired = () => {
        setSelectedExpiredContractNumbers([]);
    };

    const handleExpiredRowSelection = (selectedRows: ExpiredContract[]) => {
        const selectedNumbers = selectedRows.map(row => row.contract_number);
        setSelectedExpiredContractNumbers(selectedNumbers);
    };

    const handleSelectExpiredByDateRange = () => {
        const [fromDate, toDate] = expiredDateRange;
        if (!fromDate || !toDate) {
            message.warning(t('inventoryMovement.selectDateRangeFirst'));
            return;
        }

        setSelectedExpiredContractNumbers(selected =>
            selectInventoryContracts(expiredContracts, selected, fromDate, toDate),
        );
    };

    const handleMoveToInventory = () => {
        const allSelectedContracts = [
            ...selectedPassiveContractNumbers,
            ...selectedExpiredContractNumbers,
        ];

        if (allSelectedContracts.length === 0) {
            message.warning(t('inventoryMovement.selectAtLeastOne'));
            return;
        }

        Modal.confirm({
            title: t('inventoryMovement.confirmTitle'),
            icon: <ExclamationCircleOutlined />,
            content: (
                <div>
                    <p>{t('inventoryMovement.confirmContent')}</p>
                    <p>
                        {t('inventoryMovement.confirmTotal')}:{' '}
                        <strong>{allSelectedContracts.length}</strong>
                    </p>
                    <p>
                        - {t('inventoryMovement.confirmPassive')}:{' '}
                        <strong>{selectedPassiveContractNumbers.length}</strong>
                    </p>
                    <p>
                        - {t('inventoryMovement.confirmExpired')}:{' '}
                        <strong>{selectedExpiredContractNumbers.length}</strong>
                    </p>
                    <p>
                        {t('inventoryMovement.inventoryDate')}:{' '}
                        <strong>{inventoryDate.format('YYYY-MM-DD')}</strong>
                    </p>
                    <p style={{ marginTop: 16, fontSize: '12px', color: '#666' }}>
                        {t('inventoryMovement.note')}
                    </p>
                </div>
            ),
            okText: t('inventoryMovement.confirmOk'),
            okType: 'primary',
            cancelText: t('common.no'),
            onOk: async () => {
                try {
                    setMovingToInventory(true);
                    setLoadingPassive(true);
                    setLoadingExpired(true);
                    const result = await makeRequest<MoveToInventoryResponse>(
                        '/contracts/move-to-inventory',
                        {
                            method: 'POST',
                            body: JSON.stringify({
                                contractNumbers: allSelectedContracts,
                                inventoryDate: inventoryDate.format('YYYY-MM-DD'),
                            }),
                        },
                    );

                    if (result.success && result.data) {
                        message.success(
                            t('inventoryMovement.successCount', {
                                count: result.data.processedContracts,
                            }),
                        );
                        setSelectedPassiveContractNumbers([]);
                        setSelectedExpiredContractNumbers([]);
                        // Reload all lists to reflect changes
                        loadContracts('!');
                        loadContracts('!!');
                        loadInventoryItems();
                    } else {
                        message.error(result.error ?? t('inventoryMovement.errorMessage'));
                    }
                } catch (error) {
                    console.error(error);
                    message.error(t('inventoryMovement.networkError'));
                } finally {
                    setMovingToInventory(false);
                    setLoadingPassive(false);
                    setLoadingExpired(false);
                }
            },
        });
    };

    const handleExportGoldAnnex = async () => {
        const date = inventoryDate.format('YYYY-MM-DD');
        const result = await metals.exportGoldAnnex(date);
        if (result.success && result.data) {
            downloadBlob(result.data, `anexa_aur_${date}.xlsx`);
        } else {
            message.error(result.error ?? t('inventoryMovement.exportGoldAnnexFailed'));
        }
    };

    const handleExportElAltAnnex = async () => {
        const date = inventoryDate.format('YYYY-MM-DD');
        const result = await metals.exportElAltAnnex(date);
        if (result.success && result.data) {
            downloadBlob(result.data, `el_alt_${date}.xlsx`);
        } else {
            message.error(result.error ?? t('inventoryMovement.exportElAltAnnexFailed'));
        }
    };

    const handleExportPostponements = async () => {
        const result = await metals.exportPostponements();
        if (result.success && result.data) {
            downloadBlob(result.data, 'exportAmanari.xlsx');
        } else {
            message.error(result.error ?? t('inventoryMovement.exportPostponementsFailed'));
        }
    };

    const handleExportShortContracts = async () => {
        const result = await metals.exportShortContracts();
        if (result.success && result.data) {
            downloadBlob(result.data, 'lista_c1zi.xlsx');
        } else {
            message.error(result.error ?? t('inventoryMovement.exportShortContractsFailed'));
        }
    };

    const handleExportExpiredList = async () => {
        const [fromDate, toDate] = expiredDateRange;
        if (!fromDate || !toDate) {
            message.warning(t('inventoryMovement.selectDateRangeFirst'));
            return;
        }
        const result = await metals.exportExpiredList(
            fromDate.format('YYYY-MM-DD'),
            toDate.format('YYYY-MM-DD'),
        );
        if (result.success && result.data) {
            downloadBlob(
                result.data,
                `lista_expirate_${fromDate.format('YYYY-MM-DD')}_${toDate.format('YYYY-MM-DD')}.xlsx`,
            );
        } else {
            message.error(result.error ?? t('inventoryMovement.exportExpiredListFailed'));
        }
    };

    const handleExportExpiredObjects = async () => {
        const [fromDate, toDate] = expiredDateRange;
        if (!fromDate || !toDate) {
            message.warning(t('inventoryMovement.selectDateRangeFirst'));
            return;
        }
        const result = await metals.exportExpiredObjects(
            fromDate.format('YYYY-MM-DD'),
            toDate.format('YYYY-MM-DD'),
        );
        if (result.success && result.data) {
            downloadBlob(
                result.data,
                `obiecte_expirate_${fromDate.format('YYYY-MM-DD')}_${toDate.format('YYYY-MM-DD')}.xlsx`,
            );
        } else {
            message.error(result.error ?? t('inventoryMovement.exportExpiredObjectsFailed'));
        }
    };

    const openAnnexMerge = async () => {
        const result = await makeRequest<{ dates: string[] }>('/metals/annex-merge/dates');
        if (!result.success) {
            message.error(result.error ?? t('inventoryMovement.loadAnnexDatesFailed'));
            return;
        }

        setAnnexMergeDates(result.data?.dates ?? []);
        setAnnexMergeSelectedDates([]);
        setAnnexMergeDestinationDate(null);
        setAnnexMergePreviewItems([]);
        setAnnexMergeOpen(true);
    };

    const loadAnnexMergePreview = async (date: string) => {
        setAnnexMergePreviewLoading(true);
        const result = await makeRequest<{ items: AnnexMergePreviewItem[] }>(
            `/metals/annex-merge/preview?date=${encodeURIComponent(date)}`,
        );
        if (result.success) {
            setAnnexMergePreviewItems(result.data?.items ?? []);
        } else {
            message.error(result.error ?? t('inventoryMovement.loadAnnexContentsFailed'));
        }
        setAnnexMergePreviewLoading(false);
    };

    const handleAnnexMergeDatesChange = (selectedDates: string[]) => {
        setAnnexMergeSelectedDates(selectedDates);
        setAnnexMergeDestinationDate(currentDate =>
            currentDate && selectedDates.includes(currentDate) ? currentDate : (selectedDates[0] ?? null),
        );
    };

    const handleAnnexMergeConfirm = async () => {
        if (!annexMergeDestinationDate) {
            return;
        }

        Modal.confirm({
            title: t('inventoryMovement.confirmMerge'),
            centered: true,
            okText: t('inventoryMovement.merge'),
            onOk: async () => {
                const result = await makeRequest<{
                    success: boolean;
                    message: string;
                    mergedContracts: number;
                }>('/metals/annex-merge', {
                    method: 'POST',
                    body: JSON.stringify({
                        dates: annexMergeSelectedDates,
                        newDate: annexMergeDestinationDate,
                    }),
                });
                if (!result.success) {
                    message.error(result.error ?? t('inventoryMovement.mergeFailed'));
                    throw new Error(result.error ?? t('inventoryMovement.mergeFailed'));
                }

                message.success(result.data?.message ?? t('inventoryMovement.mergeSucceeded'));
                setAnnexMergeOpen(false);
                await loadInventoryItems();
            },
        });
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case '!!':
                return 'red';
            case '!':
                return 'orange';
            case 'INV':
                return 'blue';
            case 'EXP':
                return 'purple';
            default:
                return 'default';
        }
    };

    const getItemTypeTag = (hasMetal: number, hasElectronics: number) => {
        if (hasMetal > 0 && hasElectronics > 0) {
            return <Tag color="gold">{t('inventoryMovement.mixed')}</Tag>;
        } else if (hasMetal > 0) {
            return <Tag color="purple">{t('inventoryMovement.preciousMetals')}</Tag>;
        } else if (hasElectronics > 0) {
            return <Tag color="blue">{t('inventoryMovement.electronics')}</Tag>;
        } else {
            return <Tag>-</Tag>;
        }
    };

    const getBaseColumns = () => [
        {
            key: 'status',
            title: t('inventoryMovement.status'),
            dataIndex: 'status',
            sortable: true,
            filterable: true,
            width: 60,
            render: (status: string) => (
                <Tag
                    color={getStatusColor(status)}
                    style={{ fontWeight: 'bold', minWidth: 30, textAlign: 'center' }}
                >
                    {status}
                </Tag>
            ),
        },
        {
            key: 'contract_number',
            title: t('inventoryMovement.contractNumber'),
            dataIndex: 'contract_number',
            sortable: true,
            filterable: true,
            width: 100,
        },
        {
            key: 'client_name',
            title: t('inventoryMovement.clientName'),
            dataIndex: 'client_name',
            sortable: true,
            filterable: true,
            width: 150,
        },
        {
            key: 'pawned_item_description',
            title: t('inventoryMovement.itemDescription'),
            dataIndex: 'pawned_item_description',
            sortable: false,
            filterable: true,
        },
        {
            key: 'item_type',
            title: t('inventoryMovement.itemType'),
            dataIndex: 'item_type',
            sortable: false,
            filterable: false,
            width: 130,
            render: (value: unknown, row: { has_metals: number; has_electronics: number }) =>
                getItemTypeTag(row.has_metals, row.has_electronics),
        },
        {
            key: 'loan_amount',
            title: t('inventoryMovement.loanAmount'),
            dataIndex: 'loan_amount',
            sortable: true,
            filterable: false,
            width: 100,
            render: (value: unknown) => {
                const numValue = typeof value === 'string' ? parseFloat(value) : Number(value);
                return !isNaN(numValue) ? numValue.toFixed(0) : '-';
            },
        },
    ];

    const columns = [
        ...getBaseColumns(),
        {
            key: 'original_due_date',
            title: t('inventoryMovement.dueDate'),
            dataIndex: 'original_due_date',
            sortable: true,
            filterable: false,
            width: 100,
            render: (value: string) => (value ? dayjs(value).format('YYYY-MM-DD') : '-'),
        },
        {
            key: 'extended_due_date',
            title: t('inventoryMovement.extendedDueDate'),
            dataIndex: 'extended_due_date',
            sortable: true,
            filterable: false,
            width: 100,
            render: (value: string | null) => (value ? dayjs(value).format('YYYY-MM-DD') : '-'),
        },
    ];

    const inventoryColumns = getBaseColumns();

    // Panel styles to match Delphi look
    const panelStyle: React.CSSProperties = {
        border: '1px solid #d9d9d9',
        borderRadius: 4,
        marginBottom: 8,
        background: '#fff',
    };

    const panelHeaderStyle: React.CSSProperties = {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '3px 8px',
        borderBottom: '1px solid #d9d9d9',
        background: '#fafafa',
        fontSize: 11,
    };

    const panelTitleStyle: React.CSSProperties = {
        fontWeight: 'bold',
        fontSize: 11,
        padding: '2px 6px',
        border: '1px solid #999',
        borderRadius: 2,
        background: '#fff',
    };

    const controlsRowStyle: React.CSSProperties = {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '4px 8px',
        background: '#fafafa',
        borderBottom: '1px solid #e8e8e8',
        fontSize: 11,
    };

    // CSS for compact table styling (override MUI table cells)
    const compactTableCss = `
        .im-compact-table .MuiTableCell-root {
            padding: 2px 4px !important;
            font-size: 10px !important;
            line-height: 1.2 !important;
        }
        .im-compact-table .MuiTableCell-head {
            padding: 3px 4px !important;
            font-size: 10px !important;
        }
        .im-compact-table .MuiCheckbox-root {
            padding: 1px !important;
        }
        .im-compact-table .MuiCheckbox-root svg {
            font-size: 14px !important;
        }
        .im-compact-table .ant-tag {
            font-size: 9px !important;
            padding: 0 4px !important;
            line-height: 16px !important;
        }
    `;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 0, height: 'calc(100vh - 120px)' }}>
            <style>{compactTableCss}</style>
            <div
                className="page-header"
                style={{
                    ['--page-header-accent' as string]: '#0e7490',
                    marginBottom: 12,
                    padding: '10px 18px',
                }}
            >
                <div className="page-header__lead">
                    <span className="page-header__icon" style={{ width: 36, height: 36, fontSize: 18 }}>
                        <FileTextOutlined aria-hidden />
                    </span>
                    <div>
                        <Title level={4} style={{ margin: 0 }}>
                            {t('inventoryMovement.title')}
                        </Title>
                        <Text type="secondary">{t('inventoryMovement.subtitle')}</Text>
                    </div>
                </div>
            </div>
            <Tabs
                activeKey={activeTab}
                onChange={setActiveTab}
                type="card"
                size="small"
                style={{ flex: 1, minHeight: 0 }}
                items={[
                    {
                        key: 'movement',
                        label: t('inventoryMovement.tabs.movement'),
                        children: (
            <div style={{ display: 'flex', gap: 16, overflow: 'hidden', height: 'calc(100vh - 260px)' }}>
            {/* Main Content - Left Side */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                {/* Panel 1: Passive Contracts (!) */}
                <div style={panelStyle}>
                    <div style={panelHeaderStyle}>
                        <span style={{ ...panelTitleStyle, color: '#d46b08' }}>
                            {t('inventoryMovement.passiveContracts')} (!)
                        </span>
                        <Space>
                            <Text type="secondary">{passiveContracts.length}</Text>
                        </Space>
                    </div>
                    <div style={controlsRowStyle}>
                        <Checkbox
                            checked={
                                selectedPassiveContractNumbers.length === passiveContracts.length &&
                                passiveContracts.length > 0
                            }
                            indeterminate={
                                selectedPassiveContractNumbers.length > 0 &&
                                selectedPassiveContractNumbers.length < passiveContracts.length
                            }
                            onChange={e =>
                                e.target.checked ? selectAllPassive() : deselectAllPassive()
                            }
                        >
                            {t('inventoryMovement.selectAll')}
                        </Checkbox>
                        <div style={{ flex: 1 }} />
                        <Text>{t('inventoryMovement.fromDate')}:</Text>
                        <RangePicker
                            value={passiveDateRange}
                            onChange={handlePassiveDateRangeChange}
                            format="YYYY-MM-DD"
                            size="small"
                            style={{ width: 220 }}
                        />
                        <Button
                            size="small"
                            onClick={handleSelectPassiveByDateRange}
                            disabled={!passiveDateRange[0] || !passiveDateRange[1]}
                        >
                            {t('inventoryMovement.select')}
                        </Button>
                    </div>
                    <div className="im-compact-table" style={{ height: 110, overflow: 'hidden' }}>
                        <WebGrid<ExpiredContract>
                            data={passiveContracts}
                            rowIdAccessor={contract => contract.Nr}
                            columns={columns}
                            loading={loadingPassive}
                            pagination={false}
                            filterBar={false}
                            exportable={false}
                            selectable={true}
                            multiSelect={true}
                            showRecordCountRow={false}
                            selectedRows={passiveContracts.filter(c =>
                                selectedPassiveContractNumbers.includes(c.contract_number),
                            )}
                            onSelectionChange={handlePassiveRowSelection}
                            onRowClick={() => {}}
                            onRowDoubleClick={() => {}}
                            onExport={() => {}}
                            height="110px"
                            emptyMessage={t('inventoryMovement.noContractsFound')}
                        />
                    </div>
                </div>

                {/* Panel 2: Expired Contracts (!!) */}
                <div style={{ ...panelStyle, display: 'flex', flexDirection: 'column' }}>
                    <div style={panelHeaderStyle}>
                        <span style={{ ...panelTitleStyle, color: '#cf1322' }}>
                            {t('inventoryMovement.expiredContracts')} (!!)
                        </span>
                        <Space>
                            <Text type="secondary">{expiredContracts.length}</Text>
                            <Text strong style={{ color: '#cf1322', marginLeft: 16 }}>
                                {t('inventoryMovement.inventoryDate')}:
                            </Text>
                            <DatePicker
                                value={inventoryDate}
                                onChange={date => date && setInventoryDate(date)}
                                format="YYYY-MM-DD"
                                size="small"
                                style={{ width: 130 }}
                            />
                        </Space>
                    </div>
                    <div style={controlsRowStyle}>
                        <Checkbox
                            checked={
                                selectedExpiredContractNumbers.length === expiredContracts.length &&
                                expiredContracts.length > 0
                            }
                            indeterminate={
                                selectedExpiredContractNumbers.length > 0 &&
                                selectedExpiredContractNumbers.length < expiredContracts.length
                            }
                            onChange={e =>
                                e.target.checked ? selectAllExpired() : deselectAllExpired()
                            }
                        >
                            {t('inventoryMovement.selectAll')}
                        </Checkbox>
                        <div style={{ flex: 1 }} />
                        <Text>{t('inventoryMovement.fromDate')}:</Text>
                        <RangePicker
                            value={expiredDateRange}
                            onChange={handleExpiredDateRangeChange}
                            format="YYYY-MM-DD"
                            size="small"
                            style={{ width: 220 }}
                        />
                        <Button
                            size="small"
                            onClick={handleSelectExpiredByDateRange}
                            disabled={!expiredDateRange[0] || !expiredDateRange[1]}
                        >
                            {t('inventoryMovement.select')}
                        </Button>
                        <Button size="small" onClick={() => loadContracts('!!')}>
                            {t('inventoryMovement.reset')}
                        </Button>
                        <Button size="small" onClick={() => void handleExportPostponements()}>
                            {t('inventoryMovement.amanate')}
                        </Button>
                        <Button
                            size="small"
                            onClick={() => void handleExportExpiredList()}
                            disabled={!expiredDateRange[0] || !expiredDateRange[1]}
                        >
                            {t('inventoryMovement.imprimaLista')}
                        </Button>
                        <Button
                            size="small"
                            onClick={() => void handleExportExpiredObjects()}
                            disabled={!expiredDateRange[0] || !expiredDateRange[1]}
                        >
                            {t('inventoryMovement.imprimaObiecte')}
                        </Button>
                        <Button size="small" onClick={() => void handleExportShortContracts()}>
                            {t('inventoryMovement.contrSub30Zile')}
                        </Button>
                        <Button
                            type="primary"
                            size="small"
                            danger
                            disabled={
                                selectedPassiveContractNumbers.length === 0 &&
                                selectedExpiredContractNumbers.length === 0
                            }
                            onClick={handleMoveToInventory}
                            loading={movingToInventory}
                        >
                            {t('inventoryMovement.moveToInventory')} (
                            {selectedPassiveContractNumbers.length +
                                selectedExpiredContractNumbers.length}
                            )
                        </Button>
                    </div>
                    <div className="im-compact-table" style={{ height: 360, overflow: 'hidden' }}>
                        <WebGrid<ExpiredContract>
                            data={expiredContracts}
                            rowIdAccessor={contract => contract.Nr}
                            columns={columns}
                            loading={loadingExpired}
                            pagination={false}
                            filterBar={false}
                            exportable={false}
                            selectable={true}
                            multiSelect={true}
                            showRecordCountRow={false}
                            selectedRows={expiredContracts.filter(c =>
                                selectedExpiredContractNumbers.includes(c.contract_number),
                            )}
                            onSelectionChange={handleExpiredRowSelection}
                            onRowClick={() => {}}
                            onRowDoubleClick={() => {}}
                            onExport={() => {}}
                            height="100%"
                            emptyMessage={t('inventoryMovement.noContractsFound')}
                        />
                    </div>
                </div>

                {/* Panel 3: Items for Sale (Inventory) */}
                <div
                    style={{ ...panelStyle, height: 160, display: 'flex', flexDirection: 'column' }}
                >
                    <div style={panelHeaderStyle}>
                        <span style={{ ...panelTitleStyle, color: '#1677ff' }}>
                            {t('inventoryMovement.itemsForSale')}
                        </span>
                        <Space>
                            <Text type="secondary">
                                {inventoryItems.length} {t('inventoryMovement.items')}
                            </Text>
                            <Button size="small" onClick={() => loadInventoryItems()}>
                                {t('inventoryMovement.refresh')}
                            </Button>
                        </Space>
                    </div>
                    <div className="im-compact-table" style={{ flex: 1, overflow: 'hidden' }}>
                        <WebGrid<InventoryItem>
                            data={inventoryItems}
                            columns={inventoryColumns}
                            loading={loadingInventory}
                            pagination={false}
                            filterBar={false}
                            exportable={false}
                            selectable={false}
                            showRecordCountRow={false}
                            onSelectionChange={() => {}}
                            onRowClick={() => {}}
                            onRowDoubleClick={() => {}}
                            onExport={() => {}}
                            height="100%"
                            emptyMessage={t('inventoryMovement.noInventoryItems')}
                        />
                    </div>
                </div>
            </div>

            {/* Right Side Panel - Calendar */}
            <div style={{ width: 280, flexShrink: 0 }}>
                <div style={panelStyle}>
                    <Calendar
                        fullscreen={false}
                        value={inventoryDate}
                        onSelect={date => setInventoryDate(date)}
                    />
                </div>
                <div
                    style={{
                        ...panelStyle,
                        padding: 12,
                        background: '#fafafa',
                        marginTop: 8,
                    }}
                >
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        {t('inventoryMovement.note')}
                    </Text>
                </div>
                <Button
                    block
                    onClick={() => void handleExportGoldAnnex()}
                    style={{ marginTop: 8, marginBottom: 8 }}
                >
                    {t('inventoryMovement.anexaAur')}
                </Button>
                <Button
                    block
                    onClick={() => void handleExportElAltAnnex()}
                    style={{ marginBottom: 8 }}
                >
                    {t('inventoryMovement.elAlt')}
                </Button>
                <Button block onClick={() => void openAnnexMerge()}>
                    {t('inventoryMovement.mergeInventories')}
                </Button>
            </div>
            </div>
                        ),
                    },
                    {
                        key: 'physicalInventory',
                        label: t('inventoryMovement.tabs.physicalInventory'),
                        children:
                            activeTab === 'physicalInventory' ? (
                                <PhysicalInventoryPanel height={PHYSICAL_INVENTORY_GRID_HEIGHT} />
                            ) : null,
                    },
                ]}
            />
            <AnnexMergeModal
                open={annexMergeOpen}
                dates={annexMergeDates}
                selectedDates={annexMergeSelectedDates}
                destinationDate={annexMergeDestinationDate}
                previewItems={annexMergePreviewItems}
                previewLoading={annexMergePreviewLoading}
                onClose={() => setAnnexMergeOpen(false)}
                onSelectedDatesChange={handleAnnexMergeDatesChange}
                onDestinationDateChange={setAnnexMergeDestinationDate}
                onPreviewDate={date => void loadAnnexMergePreview(date)}
                onMerge={handleAnnexMergeConfirm}
                t={t}
            />
        </div>
    );
};

export default InventoryMovement;
